/**
 * Profily hledání — pojmenovaná nastavení CELÉHO hledání, spravovaná
 * stejně jako profily nastavení. Uživatel si u každého profilu VYBÍRÁ,
 * které části si má pamatovat (strategie, řazení+filtry, programy,
 * výběr režimů, časová okna); přepnutím se aplikují jen uložené části.
 */
import type { SortOptions } from './types';
import { defaultSortOptions } from './metrics';

export type SearchStrategy = 'default' | 'earliest' | 'latest';

export interface SearchProfileData {
  /** Každá sekce je volitelná — ukládá se jen to, co si uživatel vybral. */
  strategy?: SearchStrategy;
  sort?: SortOptions;
  programs?: { customSortId: string | null; customSearchId: string | null; customFilterIds: string[] };
  /** Id zapnutých režimů (ostatní se vypnou). */
  enabledModeIds?: string[];
  timeDep?: unknown;
  timeArr?: unknown;
}

export interface SearchProfile {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  order: number;
  visible: boolean;
  data: SearchProfileData;
  builtIn?: boolean;
}

let seq = 0;

export function defaultSearchProfiles(): SearchProfile[] {
  return [
    {
      id: 'sp-default',
      name: 'Výchozí',
      icon: 'search',
      color: '#3b82f6',
      description: 'Běžné hledání s doporučeným řazením.',
      order: 0,
      visible: true,
      data: { strategy: 'default', sort: defaultSortOptions() },
      builtIn: true,
    },
    {
      id: 'sp-earliest',
      name: 'Nejrychleji tam',
      icon: 'gauge',
      color: '#e07b16',
      description: 'Dorazit co nejdřív — řadí podle času příjezdu.',
      order: 1,
      visible: true,
      data: { strategy: 'earliest', sort: { ...defaultSortOptions(), key: 'arrival' } },
      builtIn: true,
    },
    {
      id: 'sp-latest',
      name: 'Nejpozdější odjezd',
      icon: 'clock',
      color: '#8a4fff',
      description: 'Odjet co nejpozději a stihnout okno příjezdu (zadejte „do kdy" v kartě Kdy).',
      order: 2,
      visible: true,
      data: { strategy: 'latest', sort: { ...defaultSortOptions(), key: 'departure' } },
      builtIn: true,
    },
  ];
}

export function createSearchProfile(existing: SearchProfile[]): SearchProfile {
  seq += 1;
  return {
    id: `sp-${Date.now().toString(36)}-${seq}`,
    name: 'Nové hledání',
    icon: 'star',
    color: '#0fa3a3',
    description: '',
    order: existing.reduce((m, p) => Math.max(m, p.order), -1) + 1,
    visible: true,
    data: {},
  };
}

export function sortedSearchProfiles(ps: SearchProfile[]): SearchProfile[] {
  return [...ps].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'cs'));
}

export function moveSearchProfile(ps: SearchProfile[], id: string, delta: -1 | 1): SearchProfile[] {
  const sorted = sortedSearchProfiles(ps);
  const i = sorted.findIndex((p) => p.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= sorted.length) return ps;
  [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
  return sorted.map((p, k) => ({ ...p, order: k }));
}

const STRATEGIES: SearchStrategy[] = ['default', 'earliest', 'latest'];

export function sanitizeSearchProfiles(raw: unknown): SearchProfile[] | null {
  if (!Array.isArray(raw)) return null;
  const out: SearchProfile[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const p = item as Partial<SearchProfile>;
    if (typeof p.id !== 'string' || typeof p.name !== 'string' || !p.name) continue;
    const d = (p.data && typeof p.data === 'object' ? p.data : {}) as SearchProfileData;
    out.push({
      id: p.id,
      name: p.name.slice(0, 60),
      icon: typeof p.icon === 'string' ? p.icon : 'search',
      color: typeof p.color === 'string' ? p.color : '#3b82f6',
      description: typeof p.description === 'string' ? p.description.slice(0, 300) : '',
      order: Number.isFinite(p.order) ? (p.order as number) : out.length,
      visible: p.visible !== false,
      builtIn: p.builtIn === true,
      data: {
        strategy: STRATEGIES.includes(d.strategy as SearchStrategy) ? d.strategy : undefined,
        sort: d.sort && typeof d.sort === 'object' ? (d.sort as SortOptions) : undefined,
        programs:
          d.programs && typeof d.programs === 'object'
            ? {
                customSortId: typeof d.programs.customSortId === 'string' ? d.programs.customSortId : null,
                customSearchId: typeof d.programs.customSearchId === 'string' ? d.programs.customSearchId : null,
                customFilterIds: Array.isArray(d.programs.customFilterIds)
                  ? d.programs.customFilterIds.filter((x): x is string => typeof x === 'string')
                  : [],
              }
            : undefined,
        enabledModeIds: Array.isArray(d.enabledModeIds)
          ? d.enabledModeIds.filter((x): x is string => typeof x === 'string').slice(0, 50)
          : undefined,
        timeDep: d.timeDep,
        timeArr: d.timeArr,
      },
    });
  }
  return out.length > 0 ? out : null;
}
